import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, List, ListItem, ListItemText, ListItemSecondaryAction, 
  IconButton, Chip, Divider, CircularProgress, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, Paper, Avatar, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab
} from '@mui/material';
import {
  ArrowBack, Check, Close, PersonRemove, Person, Group
} from '@mui/icons-material';
import {
  getTeamMembers, approveMember, rejectMember, removeMember, deleteTeam,
  getTeamSubscription, getTeamUsage, getSubscriptionPlans, upgradeTeamPlan
} from '../api';
import AthleteReadOnlyView from './AthleteReadOnlyView';
import SharedWaxingEntriesSection from './SharedWaxingEntriesSection';
import SharedWaxTestsSection from './SharedWaxTestsSection';
import EventsSection from './EventsSection';
import TeamWeatherDataSection from './TeamWeatherDataSection';

function RemoveMemberModal({ open, onClose, onConfirm, member, removing }) {
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setConfirmation('');
      setError('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (confirmation !== member?.name) {
      setError('Please type the exact name to confirm removal.');
      return;
    }
    onConfirm();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Remove Member</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography>
            Are you sure you want to remove <strong>{member?.name}</strong> from the team?
          </Typography>
          <TextField
            label={`Type "${member?.name}" to confirm`}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            fullWidth
            autoFocus
          />
          {error && <Typography color="error">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={removing}>Cancel</Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="error" 
          disabled={removing || confirmation !== member?.name}
        >
          {removing ? <CircularProgress size={22} color="inherit" /> : 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteTeamModal({ open, onClose, onConfirm, team, deleting }) {
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setConfirmation('');
      setError('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (confirmation !== team?.name) {
      setError('Please type the exact team name to confirm deletion.');
      return;
    }
    onConfirm();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Team</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography>
            Are you sure you want to delete <strong>{team?.name}</strong>? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All team memberships and related data will be permanently removed.
          </Typography>
          <TextField
            label={`Type "${team?.name}" to confirm`}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            fullWidth
            autoFocus
          />
          {error && <Typography color="error">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="error" 
          disabled={deleting || confirmation !== team?.name}
        >
          {deleting ? <CircularProgress size={22} color="inherit" /> : 'Delete Team'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TeamDetailView({ team, onBack, onTeamDeleted, onSkiRequestSubmitted }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planLoading, setPlanLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [upgrading, setUpgrading] = useState(false);
  const [removeModal, setRemoveModal] = useState(false);
  const [removingMember, setRemovingMember] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [viewAthlete, setViewAthlete] = useState(null);
  const [tab, setTab] = useState(0);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTeamMembers(team.id);
      setMembers(data);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    } finally {
      setLoading(false);
    }
  }, [team.id]);

  useEffect(() => {
    // Only fetch members if user is a coach (not an athlete)
    if (team.role !== 'athlete') {
      fetchMembers();
    } else {
      // For athletes, just set loading to false since we don't need members
      setLoading(false);
    }
  }, [fetchMembers, team.role]);

  const fetchPlanData = useCallback(async () => {
    setPlanLoading(true);
    try {
      const [sub, usageRes, plansRes] = await Promise.all([
        getTeamSubscription(team.id),
        getTeamUsage(team.id),
        getSubscriptionPlans()
      ]);
      setSubscription(sub);
      setUsage(usageRes.usage || usageRes);
      setPlans(plansRes);
    } catch (err) {
      console.error('Failed to fetch plan data:', err);
    } finally {
      setPlanLoading(false);
    }
  }, [team.id]);

  useEffect(() => {
    fetchPlanData();
  }, [fetchPlanData]);

  const handleApprove = async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: true }));
    try {
      await approveMember(team.id, userId);
      await fetchMembers();
    } catch (err) {
      console.error('Failed to approve member:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleReject = async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: true }));
    try {
      await rejectMember(team.id, userId);
      await fetchMembers();
    } catch (err) {
      console.error('Failed to reject member:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleRemove = (member) => {
    setRemovingMember(member);
    setRemoveModal(true);
  };

  const handleRemoveConfirm = async () => {
    setRemoving(true);
    try {
      await removeMember(team.id, removingMember.id);
      await fetchMembers();
      setRemoveModal(false);
      setRemovingMember(null);
    } catch (err) {
      console.error('Failed to remove member:', err);
    } finally {
      setRemoving(false);
    }
  };

  const handleDeleteTeam = () => {
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteTeam(team.id);
      onTeamDeleted();
    } catch (err) {
      console.error('Failed to delete team:', err);
    } finally {
      setDeleting(false);
    }
  };

  const approvedCoaches = members.filter(m => m.status === 'approved' && m.role === 'coach');
  const approvedAthletes = members.filter(m => m.status === 'approved' && m.role === 'athlete');
  const pendingMembers = members.filter(m => m.status === 'pending');

  const isAthleteOnly = team.role === 'athlete';

  if (isAthleteOnly) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
        {/* Back arrow and team info remain unchanged */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={onBack}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h4">{team.name}</Typography>
          </Box>
          {/* Delete Team button hidden for athletes */}
        </Box>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Events" />
        </Tabs>
        {tab === 0 && <EventsSection team={team} athleteView onSkiRequestSubmitted={onSkiRequestSubmitted} />}
      </Box>
    );
  }

  // Coach/both: full tab set
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
      {/* Back arrow and team info remain unchanged */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={onBack}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">{team.name}</Typography>
        </Box>
        {/* Show Delete Team button only for coaches/both */}
        <Button 
          variant="contained" 
          color="error" 
          onClick={handleDeleteTeam}
          startIcon={<PersonRemove />}
        >
          Delete Team
        </Button>
      </Box>

      {team.description && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body1">{team.description}</Typography>
        </Paper>
      )}

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Members & Athletes" />
        <Tab label="Shared Waxing Diary" />
        <Tab label="Shared Wax Tests" />
        <Tab label="Events" />
        <Tab label="Weather Data" />
        <Tab label="Plan & Limits" />
      </Tabs>
      {tab === 0 && (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {/* Coaches */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Group color="primary" />
                    <Typography variant="h6">Team Coaches ({approvedCoaches.length})</Typography>
                  </Box>
                  {approvedCoaches.length === 0 ? (
                    <Typography color="text.secondary">No approved coaches yet.</Typography>
                  ) : (
                    <List>
                      {approvedCoaches.map((member, index) => (
                        <React.Fragment key={member.id}>
                          <ListItem alignItems="flex-start">
                            <Avatar sx={{ mr: 2 }}>
                              <Person />
                            </Avatar>
                            <ListItemText
                              primary={member.name}
                              secondary={member.email}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                              <Chip 
                                label={member.role} 
                                size="small" 
                                color="primary"
                              />
                            </Box>
                            <ListItemSecondaryAction>
                              <IconButton 
                                color="error" 
                                onClick={() => handleRemove(member)}
                                disabled={approvedCoaches.length <= 1}
                              >
                                <PersonRemove />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < approvedCoaches.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>

              {/* Pending Requests */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Group color="warning" />
                    <Typography variant="h6">Pending Requests ({pendingMembers.length})</Typography>
                  </Box>
                  {pendingMembers.length === 0 ? (
                    <Typography color="text.secondary">No pending requests.</Typography>
                  ) : (
                    <List>
                      {pendingMembers.map((member, index) => (
                        <React.Fragment key={member.id}>
                          <ListItem alignItems="flex-start">
                            <Avatar sx={{ mr: 2 }}>
                              <Person />
                            </Avatar>
                            <ListItemText
                              primary={member.name}
                              secondary={member.email}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 70, mr: 2 }}>
                              <Chip 
                                label={member.role} 
                                size="small" 
                                color={member.role === 'coach' ? 'primary' : 'default'}
                                sx={{ mb: 0.5 }}
                              />
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                <IconButton 
                                  color="success" 
                                  onClick={() => handleApprove(member.id)}
                                  disabled={actionLoading[member.id]}
                                  size="small"
                                >
                                  {actionLoading[member.id] ? <CircularProgress size={20} /> : <Check />}
                                </IconButton>
                                <IconButton 
                                  color="error" 
                                  onClick={() => handleReject(member.id)}
                                  disabled={actionLoading[member.id]}
                                  size="small"
                                >
                                  {actionLoading[member.id] ? <CircularProgress size={20} /> : <Close />}
                                </IconButton>
                              </Box>
                            </Box>
                          </ListItem>
                          {index < pendingMembers.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Athletes Table */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Team Athletes ({approvedAthletes.length})</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="center">Remove</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approvedAthletes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>
                          No athletes in this team.
                        </TableCell>
                      </TableRow>
                    ) : (
                      approvedAthletes.map((athlete) => (
                        <TableRow key={athlete.id} hover style={{ cursor: 'pointer' }} onClick={() => setViewAthlete(athlete)}>
                          <TableCell>{athlete.name}</TableCell>
                          <TableCell>{athlete.email}</TableCell>
                          <TableCell align="center">
                            <IconButton 
                              color="error" 
                              onClick={e => { e.stopPropagation(); handleRemove(athlete); }}
                            >
                              <PersonRemove />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )
      )}
      {tab === 1 && <SharedWaxingEntriesSection team={team} />}
      {tab === 2 && <SharedWaxTestsSection team={team} />}
      {tab === 3 && (<EventsSection team={team} onSkiRequestSubmitted={onSkiRequestSubmitted} />)}
      {tab === 4 && <TeamWeatherDataSection team={team} />}
      {tab === 5 && (
        <Box>
          {planLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Current Plan</Typography>
                  {!subscription ? (
                    <Typography color="text.secondary">No active subscription found.</Typography>
                  ) : (
                    <>
                      <Typography><strong>{subscription.plan_name}</strong> ({subscription.billing_cycle})</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Period: {new Date(subscription.current_period_start).toLocaleDateString()} → {new Date(subscription.current_period_end).toLocaleDateString()}
                      </Typography>
                    </>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>Usage</Typography>
                  {!usage ? (
                    <Typography color="text.secondary">No usage data.</Typography>
                  ) : (
                    <>
                      <Typography>Coaches: {usage.coaches.current} / {usage.coaches.max}</Typography>
                      <Typography>Athletes: {usage.athletes.current} / {usage.athletes.max}</Typography>
                    </>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Upgrade Plan</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose a higher plan if you need more seats.
                  </Typography>
                  <List>
                    {plans.map(p => (
                      <ListItem key={p.id} disableGutters secondaryAction={
                        <Button 
                          variant="contained"
                          disabled={upgrading || (subscription && p.id === subscription.plan_id)}
                          onClick={async () => {
                            setUpgrading(true);
                            try {
                              await upgradeTeamPlan(team.id, p.id, subscription?.billing_cycle);
                              await fetchPlanData();
                            } catch (e) {
                              console.error('Upgrade failed:', e);
                            } finally {
                              setUpgrading(false);
                            }
                          }}
                        >
                          {subscription && p.id === subscription.plan_id ? 'Current' : (upgrading ? 'Upgrading...' : 'Upgrade')}
                        </Button>
                      }>
                        <ListItemText 
                          primary={`${p.name} — ${Number(p.monthly_price).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}/mo`}
                          secondary={`Coaches: ${p.max_coaches}, Athletes: ${p.max_athletes}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      <RemoveMemberModal 
        open={removeModal} 
        onClose={() => setRemoveModal(false)} 
        onConfirm={handleRemoveConfirm} 
        member={removingMember} 
        removing={removing} 
      />
      <DeleteTeamModal 
        open={deleteModal} 
        onClose={() => setDeleteModal(false)} 
        onConfirm={handleDeleteConfirm} 
        team={team} 
        deleting={deleting} 
      />
      {viewAthlete && (
        <AthleteReadOnlyView
          open={!!viewAthlete}
          onClose={() => setViewAthlete(null)}
          athlete={viewAthlete}
          team={team}
        />
      )}
    </Box>
  );
} 